import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import WizardWrapper from './WizardWrapper';
import CreateClassForm from './CreateClassForm';
import ConfirmDialog from './ConfirmDialog';
import { classFieldErrors, classInputToPayload, parseDay } from "./helpers";
import { useAppState } from "./AppStateContext";

const EditClass = ({ classData, open, onClose }) => {
    const { classes, fetchClasses, apiDelete, apiPut } = useAppState();
    const navigate = useNavigate();
    const defaultUserInput = { name: '', level: '', term: '', start_date: null, end_date: null };
    const [userInput, setUserInput] = useState(defaultUserInput);
    const [nameError, setNameError] = useState(false);
    const [startDateError, setStartDateError] = useState(false);
    const [endDateError, setEndDateError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    useEffect(() => {
        if (classData) {
            setUserInput({
                name: classData.name || '',
                level: classData.level || '',
                term: classData.term || '',
                start_date: classData.start_date ? parseDay(classData.start_date) : null,
                end_date: classData.end_date ? parseDay(classData.end_date) : null,
            });
        }
    }, [classData, open]);

    const handleClose = (event, reason) => {
        setUserInput(defaultUserInput);
        setNameError(false);
        setStartDateError(false);
        setEndDateError(false);
        setErrorMessage("");
        onClose(reason === 'created');
    };

    const handleDelete = async () => {
        try {
            await apiDelete(`classes/${classData.uuid}`);
            fetchClasses();
            // Leave the page of the deleted class directly, instead of letting
            // the parent refetch it and react to the resulting 404.
            handleClose(null);
            navigate('/classes');
        } catch (error) {
            setErrorMessage("An error occurred while trying to delete the class.");
        }
    };

    const handleSave = async () => {
        try {
            await apiPut(`classes/${classData.uuid}`, classInputToPayload(userInput));
            fetchClasses();
            handleClose(null, 'created');
        } catch (error) {
            // Field validation failures land on their fields; anything else
            // shows the server's message, which can carry a reason the form
            // cannot know, such as the license seats an activation would need.
            // Both branches set every error channel, so nothing stale from an
            // earlier failed save lingers alongside the fresh errors.
            const responseData = error.response?.data;
            if (error.response?.status === 422 && responseData?.errors) {
                const fieldErrors = classFieldErrors(responseData.errors);
                setNameError(fieldErrors.name);
                setStartDateError(fieldErrors.start_date);
                setEndDateError(fieldErrors.end_date);
                setErrorMessage(fieldErrors.other || "");
            } else {
                setNameError(false);
                setStartDateError(false);
                setEndDateError(false);
                setErrorMessage(responseData?.message
                    || "An error occurred while trying to modify the class.");
            }
        }
    };

    return (
        <WizardWrapper open={open} onClose={handleClose} onBack={null} canGoBack={false}>
            <CreateClassForm
                editMode={true}
                classes={classes.filter(schoolClass => schoolClass.uuid !== classData?.uuid)}
                userInput={userInput}
                setUserInput={(input) => { setErrorMessage(""); setUserInput(input); }}
                nameError={nameError}
                setNameError={setNameError}
                startDateError={startDateError}
                setStartDateError={setStartDateError}
                endDateError={endDateError}
                setEndDateError={setEndDateError}
                setActiveStep={null}
                onDelete={() => setConfirmDeleteOpen(true)}
                onSave={handleSave}
                onCancel={handleClose}
                errorMessage={errorMessage}
            />
            <ConfirmDialog
                open={confirmDeleteOpen}
                title="Delete class"
                message={`Do you really want to delete the class “${classData?.name}”?`}
                confirmLabel="Delete"
                onConfirm={() => { setConfirmDeleteOpen(false); handleDelete(); }}
                onCancel={() => setConfirmDeleteOpen(false)}
            />
        </WizardWrapper>
    );
};

export default EditClass;
